<template>
  <div class="transaction-list">
    <table>
      <thead>
        <th>{{ $t('ID') }}</th>
        <th>{{ $t('TRANSACTION_DATE') }}</th>
        <th>{{ $t('TRANSACTION_TYPE') }}</th>
        <th :colspan="[!readOnly ? '1' : '2']">{{ $t('AMOUNT') }}</th>
        <th class="actions" v-if="!readOnly">
          <font-awesome-icon class="action-icon" :title="$t('ADD_TRANSACTION')" :icon="faFolderPlus" @click="addNewTransaction()" />
        </th>
      </thead>
      <tbody>
        <template v-for="(transaction, index) in transactions">
          <tr :key="transaction.id" v-if="!!transaction.status" class="registered" :class="{ retracted: (transaction.status === TRANSACTION_STATUSES.RETRACTED) }">
            <td>{{ (transactions.length - index) }}</td>
            <td>{{ transaction.registeredOn }}</td>
            <td>{{ $t(transaction.type) }}</td>
            <td>{{ addCommas(Math.round(parseFloat(transaction.amount) * MILLION)) }}</td>
            <td class="actions" v-if="undo">
              <font-awesome-icon class="action-icon" :title="$t('REMOVE_TRANSACTION')" :icon="faTrashAlt" @click="removeRegisteredTransaction(index)" v-if="!transaction.parentId && (transaction.status === TRANSACTION_STATUSES.REGISTERED) && (transaction.action === TRANSACTION_ACTIONS.REPAY || transaction.action === TRANSACTION_ACTIONS.WAIVE)" />
            </td>
          </tr>
          <tr :key="transaction.id" v-if="!transaction.status">
            <td>{{ (transactions.length - index) }}</td>
            <td class="input-wrapper">
              <date-input
                label=" "
                v-model="transaction.registeredOn"
                borderbottom
                required
                :validate="validateTransactionDate"
                @input="sortTransactions()"
                ref="registeredOnInputs"
              />
            </td>
            <td class="input-wrapper">
              <select-input
                label=" "
                v-model="transaction.type"
                :items="types"
                :placeholder="$t('SELECT_ONE')"
                borderbottom
                required
                @input="sortTransactions()"
                ref="typeInputs"
              />
            </td>
            <td class="input-wrapper">
              <text-input
                label=" "
                v-model="transaction.amount"
                :hint="changeDigitToViText(transaction.amount)"
                borderbottom
                required
                ref="amountInputs"
              />
            </td>
            <td class="actions" v-if="!readOnly">
              <font-awesome-icon class="action-icon" :title="$t('REMOVE_TRANSACTION')" :icon="faTrashAlt" @click="removeNewTransaction(index)" />
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <confirm-modal />
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTrashAlt, faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { TRANSACTION_TYPES, TRANSACTION_STATUSES, TRANSACTION_ACTIONS, DATE_FORMAT, DISPLAY_DATE_FORMAT, MILLION } from '@/consts';
import { changeDigitToViText, addCommas } from '@/helpers';

import DateInput from '@/components/DateInput';
import SelectInput from '@/components/SelectInput';
import TextInput from '@/components/TextInput';
import ConfirmModal from '@/components/ConfirmModal';

import TransactionListService from './TransactionListService';

const { useActions: useConfirmActions } = createNamespacedHelpers('confirm');
const { useActions: useModalActions } = createNamespacedHelpers('modal');

const newTransaction = {
  status: null,
  amount: 0,
  type: null,
  registeredOn: moment().format(DISPLAY_DATE_FORMAT)
};

export default {
  name: 'TransactionList',
  components: {
    FontAwesomeIcon,
    DateInput,
    SelectInput,
    TextInput,
    ConfirmModal
  },
  props: {
    contractId: {
      type: [String, Number],
      required: true
    },
    disbursedOn: {
      type: String,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    undo: {
      type: Boolean,
      default: false,
    }
  },
  setup(props, { root, emit }) {
    const { setTitleModal, setOnConfirm } = useConfirmActions(['setTitleModal', 'setOnConfirm']);
    const { toggleConfirmModal: toggleConfirm } = useModalActions(['toggleConfirmModal']);

    const transactions = ref([]);
    const lastRegisteredTransactionDate = ref(null);
    const contractId = computed(() => props.contractId);

    const registeredOnInputs = ref(null);
    const typeInputs = ref(null);
    const amountInputs = ref(null);

    const types = Object.keys(TRANSACTION_TYPES).map((key) => ({
      value: key,
      text: root.$t(key)
    }));

    const fetchTransactions = async () => {
      const items = await TransactionListService.getTransactionList(contractId.value);

      let items_ = items.filter((i) => (
        (i.action === TRANSACTION_ACTIONS.REPAY)
        || (i.action === TRANSACTION_ACTIONS.RENEW)
        || (
          (i.action === TRANSACTION_ACTIONS.WAIVE)
          && !i.isRenewalDifference
        )
      )).map((i) => (
        (i.action === TRANSACTION_ACTIONS.WAIVE)
          ? {
            ...i,
            type: 'WAIVE'
          }
          : i
      ));

      let transactionsArr = [];

      if (!props.readOnly) {
        transactionsArr.push({
          ...newTransaction,
          id: uuidv4()
        });
      }

      transactionsArr = transactionsArr.concat([...items_.reverse().map((t) => ({
        ...t,
        registeredOn: moment(t.registeredOn, DATE_FORMAT).format(DISPLAY_DATE_FORMAT),
        amount: (t.amount / MILLION).toFixed(3)
      }))]);

      transactions.value = transactionsArr;

      items_ = items_.filter((i) => (i.status === TRANSACTION_STATUSES.REGISTERED));
      if (items_.length) {
        lastRegisteredTransactionDate.value = items_.reverse()[0].registeredOn;
      }
    };

    const sortTransactions = () => {
      transactions.value = transactions.value.sort((a, b) => {
        const aRegisteredOn = moment(a.registeredOn, DISPLAY_DATE_FORMAT);
        const bRegisteredOn = moment(b.registeredOn, DISPLAY_DATE_FORMAT);

        if (aRegisteredOn < bRegisteredOn) {
          return 1;
        }

        if (aRegisteredOn > bRegisteredOn) {
          return -1;
        }

        if (!a.status && !!b.status) {
          return -1;
        }

        if (!b.status && !!a.status) {
          return 1;
        }

        return 0;
      });
    };

    const addNewTransaction = () => {
      transactions.value = [
        {
          ...newTransaction,
          id: uuidv4()
        },
        ...transactions.value
      ];
    };

    const removeNewTransaction = (i) => {
      transactions.value = [
        ...transactions.value.slice(0, i),
        ...transactions.value.slice(i + 1, transactions.value.length)
      ];
    };

    const removeRegisteredTransaction = (i) => {
      toggleConfirm(true);
      setTitleModal(root.$t('ARE_YOU_SURE'));
      setOnConfirm(async () => {
        const transaction = transactions.value[i];
        await TransactionListService.retractTransaction(contractId.value, transaction.id);
        transaction.status = TRANSACTION_STATUSES.RETRACTED;
        emit('remove-transaction');
        sortTransactions();
      });
    };

    const validateTransactionDate = (val) => {
      const date = moment(val, DISPLAY_DATE_FORMAT, true).startOf('day');

      if (date.isValid()) {
        const disbursementDate = moment(props.disbursedOn, DATE_FORMAT).startOf('day');
        const today = moment().startOf('day');
        const lim = lastRegisteredTransactionDate.value ? moment(lastRegisteredTransactionDate.value, DATE_FORMAT).startOf('day') : disbursementDate;

        if (
          (disbursementDate > date)
          || (today < date)
          || (date < lim)
        ) {
          return 'INVALID_TRANSACTION_DATE';
        }

        return '';
      }

      return 'INVALID_DATE_FORMAT';
    };

    const validate = () => {
      let valid = true;

      const inputs = [
        ...registeredOnInputs.value,
        ...typeInputs.value,
        ...amountInputs.value
      ];

      inputs.forEach((input) => {
        const v = input.validateInput();
        valid = valid && v;
      });

      return valid;
    };

    watch(transactions, (val) => {
      emit('input', val);
    });

    watch(contractId, () => {
      fetchTransactions();
    }, {
      immediate: true
    });

    return {
      moment,
      DATE_FORMAT,
      DISPLAY_DATE_FORMAT,
      TRANSACTION_STATUSES,
      TRANSACTION_ACTIONS,
      MILLION,
      transactions,
      types,
      changeDigitToViText,
      faFolderPlus,
      faTrashAlt,
      addNewTransaction,
      removeNewTransaction,
      removeRegisteredTransaction,
      validateTransactionDate,
      sortTransactions,
      addCommas,
      fetchTransactions,
      validate,
      registeredOnInputs,
      typeInputs,
      amountInputs
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

.transaction-list {
  table {
    width: 100%;

    .actions {
      text-align: right;
    }

    .action-icon {
      color: $blueNcs;
      transition: all .2s ease-in-out;
      cursor: pointer;
      font-size: 24px;

      &:hover {
        color: $paradisePink;
      }
    }

    thead {
      th {
        padding: 3px 7px;
      }
    }

    tbody {
      .action-icon {
        font-size: 18px;
      }

      tr {
        td {
          padding: 3px 7px;

          &.input-wrapper {
            width: 300px;
            vertical-align: top;
          }

          .input {
            width: 100%;
          }

          &:first-child {
            padding-left: 15px;
          }
        }

        &.registered {
          td {
            background-color: $ghostWhite;
            padding: 7px;
            border-bottom: 2px solid $babyPowder;

            &:first-child {
              border-top-left-radius: 4px;
              border-bottom-left-radius: 4px;
              padding-left: 15px;
            }

            &:last-child {
              border-top-right-radius: 4px;
              border-bottom-right-radius: 4px;
            }
          }

          &.retracted {
            text-decoration: line-through;
            color: $paradisePink;
          }
        }
      }
    }
  }
}
</style>
